import { ChangeEvent, useContext, useEffect, useState } from "react";

import { Trans, useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import Dialog from "components/DesignComponents/Dialog";
import TableContext from "context/table/tableContext";
import { useApi } from "shared/hooks/useApi";
import { useMediaQueries } from "shared/hooks/useMediaQueries";
import { maxDialogWidth } from "shared/styles";
import { ENDPOINTS } from "shared/types/enums";

import { isFtPlatform, isUnknownPlatform } from "../../Details";
import { FinalStepButtons, FirstStepButtons } from "./Buttons";
import {
    FMX_QUERY,
    FT_QUERY,
    INVALID_FMX_LOCK_CODE,
    INVALID_FT_MAIN_KEY,
    LIMIT_REACHED
} from "./constants";
import GenerateKeyContent from "./GenerateKeyContent";
import { GenerateUnlockKeyDialogProps, SelectablePlatform } from "./types";

const GenerateUnlockKeyDialog = ({
    isOpen,
    closeDialog
}: GenerateUnlockKeyDialogProps) => {
    const {
        detailsData: { imei, model_platform }
    } = useContext(TableContext);

    const { t } = useTranslation();
    const { toMd, fromMd } = useMediaQueries();
    const { getData, getCriticalMessage, handleResponse } = useApi();

    const [selectedPlatform, setSelectedPlatform] =
        useState<SelectablePlatform | null>(
            isUnknownPlatform(model_platform) ? null : model_platform
        );

    const [isLoading, setIsLoading] = useState(false);
    const [lockCode, setLockCode] = useState("");
    const [unlockKey, setUnlockKey] = useState("");
    const [isInvalidLockCode, setIsInvalidLockCode] = useState(false);
    const [isLimitReached, setIsLimitReached] = useState(false);

    useEffect(() => {
        setSelectedPlatform(
            isUnknownPlatform(model_platform) ? null : model_platform
        );
    }, [model_platform]);

    const handlePlatformChange = (platform: SelectablePlatform | null) => {
        setSelectedPlatform(platform);

        if (platform === null) {
            setLockCode("");
            setIsInvalidLockCode(false);
            setIsLimitReached(false);
        }
    };

    const handleLockCodeValueChange = (e: ChangeEvent<HTMLInputElement>) => {
        setIsInvalidLockCode(false);
        setLockCode(e.target.value.trim());
    };

    const handleSecondaryBtnClick = () => {
        if (unlockKey) {
            setUnlockKey("");
        } else {
            isUnknownPlatform(model_platform)
                ? handlePlatformChange(null)
                : closeDialog();
        }
    };

    const submitLockCode = async () => {
        try {
            setIsLoading(true);

            const unlockKeyQuery = isFtPlatform(selectedPlatform!)
                ? FT_QUERY
                : FMX_QUERY;

            const { data } = await getData(
                `${ENDPOINTS.Devices}/${imei}/unlockKey/${unlockKeyQuery}=${lockCode}`
            );

            setUnlockKey(data);
        } catch (error) {
            const { message } = getCriticalMessage(error);

            const isLimitReached = message === LIMIT_REACHED;
            const isInvalidFtMainKey = message === INVALID_FT_MAIN_KEY;
            const isInvalidFmxLockCode = message === INVALID_FMX_LOCK_CODE;

            const isInvalidLockCode =
                isInvalidFtMainKey || isInvalidFmxLockCode;

            if (isLimitReached) {
                setLockCode("");
                setIsLimitReached(true);
            } else if (isInvalidLockCode) {
                setIsInvalidLockCode(true);
            } else {
                handleResponse(error);
            }
        }

        setIsLoading(false);
    };

    const isPlatformNotSelected = selectedPlatform === null;

    return (
        <Dialog
            isTitleSeparator
            isActionsSeparator={!isPlatformNotSelected}
            title={
                isPlatformNotSelected
                    ? t("Dialog##unlocker##select device platform")
                    : t("Dialog##unlocker##generate unlock key")
            }
            description={
                isPlatformNotSelected ? (
                    <div
                        css={css({
                            "& > span": {
                                fontWeight: "700"
                            }
                        })}
                    >
                        <Trans
                            i18nKey="Dialog##unlocker##select device platform description"
                            components={{
                                1: <span />,
                                2: <span />,
                                3: <span />
                            }}
                        />
                    </div>
                ) : (
                    t("Dialog##unlocker##generate unlock key description")
                )
            }
            open={isOpen}
            close={closeDialog}
            submit={submitLockCode}
            actions={
                isPlatformNotSelected ? (
                    <FirstStepButtons changePlatform={handlePlatformChange} />
                ) : (
                    <FinalStepButtons
                        isLoading={isLoading}
                        isLimitReached={isLimitReached}
                        unlockKey={unlockKey}
                        lockCode={lockCode}
                        onSecondaryBtnClick={handleSecondaryBtnClick}
                    />
                )
            }
            slotProps={{
                transition: {
                    onExited: () => {
                        setSelectedPlatform(
                            isUnknownPlatform(model_platform)
                                ? null
                                : model_platform
                        );

                        setIsLoading(false);
                        setLockCode("");
                        setUnlockKey("");
                        setIsInvalidLockCode(false);
                        setIsLimitReached(false);
                    }
                }
            }}
            extendToMaxWidth={toMd}
            css={css(fromMd && maxDialogWidth)}
        >
            {isPlatformNotSelected ? null : (
                <GenerateKeyContent
                    isLimitReached={isLimitReached}
                    isInvalidLockCode={isInvalidLockCode}
                    isLoading={isLoading}
                    unlockKey={unlockKey}
                    lockCode={lockCode}
                    handleLockCodeValueChange={handleLockCodeValueChange}
                />
            )}
        </Dialog>
    );
};

export default GenerateUnlockKeyDialog;
